import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { SweetAlertService } from 'src/app/services/sweetalert.service';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignupComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  signupForm!: FormGroup;
  isLoading = false;
  showPassword = false;
  showConfirmPassword = false;
  showReferralCode = false;
  passwordStrength = 0;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private sweetAlertService: SweetAlertService,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.handleInviteToken();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(): void {
    this.signupForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(10)]],
      email: ['', [Validators.required, Validators.email]],
      phone_number: ['', [Validators.required, Validators.pattern(/^[0-9+\s()-]+$/)]],
      telegram_username: ['', [Validators.required]],
      referral_code: [''],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]],
      sex: ['', [Validators.required]],
      acceptTerms: [false, [Validators.requiredTrue]]
    }, {
      validators: this.passwordMatchValidator
    });

    // Watch password changes for strength indicator
    this.signupForm.get('password')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(password => {
        this.passwordStrength = this.calculatePasswordStrength(password);
      });
  }

  handleInviteToken(): void {
  const inviteToken = this.route.snapshot.queryParamMap.get('invite');

  if (inviteToken) {
    this.showReferralCode = true;
    this.signupForm.get('referral_code')?.setValue(inviteToken);
    this.signupForm.get('referral_code')?.disable();
  }
}


  passwordMatchValidator(formGroup: FormGroup) {
    const password = formGroup.get('password')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  calculatePasswordStrength(password: string): number {
    if (!password) return 0;

    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 10;

    return Math.min(strength, 100);
  }

  getPasswordStrengthLabel(): string {
    if (this.passwordStrength < 25) return 'Faible';
    if (this.passwordStrength < 50) return 'Moyen';
    if (this.passwordStrength < 75) return 'Bon';
    return 'Excellent';
  }

  getPasswordStrengthClass(): string {
    if (this.passwordStrength < 25) return 'weak';
    if (this.passwordStrength < 50) return 'medium';
    if (this.passwordStrength < 75) return 'good';
    return 'strong';
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  toggleReferralCode(): void {
  this.showReferralCode = !this.showReferralCode;

  if (!this.showReferralCode) {
    this.signupForm.get('referral_code')?.reset();
  }
}


  onSubmit(): void {
    if (this.signupForm.valid) {
      this.isLoading = true;

      // 🔹 Créer un payload sans acceptTerms et confirmPassword
      const { acceptTerms, confirmPassword, referral_code, ...rest } = this.signupForm.value;

      const payload: any = { ...rest };

      if (referral_code) {
        payload.referral_code = referral_code;
      }


      this.authService.register(payload).subscribe({
        next: () => {
          this.isLoading = false;

          this.sweetAlertService.toastSuccess(
            "Inscription reussie. Un email d'activation vous a ete envoye. Activez votre compte avant de vous connecter.",
            6000
          );

          this.resetForm();

          this.router.navigate(['/auth/signin'], {
            queryParams: {
              activation_required: '1',
              email: payload.email || ''
            }
          });
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;

          this.authService.onAuthFailure(error);

          this.sweetAlertService.toastError(
            'Erreur !',
            5000,
            (error.error?.detail || error.error?.error) ||
            'Le service est temporairement indisponible'
          );
        },
      });

    } else {
      Object.keys(this.signupForm.controls).forEach(key => {
        this.signupForm.get(key)?.markAsTouched();
      });
    }
  }


  resetForm() {
    this.signupForm.reset();
    Object.keys(this.signupForm.controls).forEach(c => {
      this.signupForm.controls[c].setErrors(null);
    });
  }


  getFieldError(fieldName: string): string {
    const field = this.signupForm.get(fieldName);

    if (field?.hasError('required')) {
      return 'Ce champ est requis';
    }
    if (field?.hasError('email')) {
      return 'Email invalide';
    }
    if (field?.hasError('minlength')) {
      const minLength = field.errors?.['minlength'].requiredLength;
      return `Minimum ${minLength} caractères requis`;
    }
    if (field?.hasError('pattern')) {
      return 'Format invalide';
    }

    if (fieldName === 'confirmPassword' && this.signupForm.hasError('passwordMismatch')) {
      return 'Les mots de passe ne correspondent pas';
    }

    return '';
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.signupForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  onSignin(): void {
    this.router.navigate(['/auth/signin']);
  }

  onShowMore(): void {
    this.router.navigate(['/']);
  }
}
